/* @jsx h */
import { computed, defineComponent, ref, h } from "vue";
import { useToast } from "../../providers/Toast";
import { PaymentReceiptMain, PaymentReceiptProps } from "./PaymentReceiptMain";
import { EmailModal } from "./RequestPaymentEmailModal";
import { useLocalization } from "@lemmonhq/uikit";
//
export const PaymentReceipt = defineComponent<PaymentReceiptProps>({
  name: "PaymentReceipt",
  props: ["payment", "shop", "orders", "relatedPayment"],
  setup(props, { slots }) {
    const { showToast } = useToast();
    const showEmailModal = ref(false);
    const { locale, t } = useLocalization();

    const currency = computed(() => props.shop.currency);
    const receiptLabels = computed(() => ({
      receipt: t("customer.receipt.receipt"),
      creditNote: t("customer.receipt.credit_note"),
      tel: t("customer.receipt.tel"),
      vat: t("customer.receipt.vat"),
      paymentMethod: t("customer.receipt.payment_method"),
      table: t("customer.receipt.table"),
      transactionId: t("customer.receipt.transaction_id"),
      type: t("customer.receipt.type"),
      status: t("customer.receipt.status"),
      items: t("customer.receipt.items"),
      taxRate: t("customer.receipt.tax_rate"),
      creditNoteInformation: t("customer.receipt.credit_note_information"),
      creditNoteForPayment: t("customer.receipt.credit_note_for_payment"),
      splitPayment: t("customer.receipt.split_payment"),
      splitPaymentCopy: t("customer.receipt.split_payment_copy"),
      ofTotal: t("customer.receipt.of_total"),
      partialPayment: t("customer.receipt.partial_payment"),
      partialPaymentCopy: t("customer.receipt.partial_payment_copy"),
      vatBreakdown: t("customer.receipt.vat_breakdown"),
      net: t("customer.receipt.net"),
      subtotal: t("customer.receipt.subtotal"),
      discount: t("customer.receipt.discount"),
      tips: t("customer.receipt.tips"),
      rewards: t("customer.receipt.rewards"),
      deliveryFee: t("customer.receipt.delivery_fee"),
      total: t("customer.receipt.total"),
      totalPaid: t("customer.receipt.total_paid"),
    }));

    const handleEmailSend = async (email: string) => {
      try {
        // TODO: Implement actual email sending
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call
        showToast(t("customer.receipt.sent_success"), { type: "success" });
      } catch (e) {
        showToast(t("customer.receipt.sent_error"), {
          type: "error",
        });
        throw e;
      }
    };

    /** A labelled row with its own icon, so the sidebar scans rather than reads. */
    const detailRow = (icon: string, label: string, value: any) => (
      <div class="flex items-start gap-3">
        <span class="material-symbols-outlined text-lg text-base-content/40 shrink-0 mt-0.5">
          {icon}
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-xs text-base-content/50">{label}</p>
          <div class="text-sm text-base-content break-words">{value}</div>
        </div>
      </div>
    );

    return () => (
      <div class="min-h-screen py-6 sm:py-8">
        <div class="container mx-auto px-4 max-w-6xl">
          {/*
            Three blocks, ordered differently per breakpoint:

              mobile          desktop
              ──────          ───────
              offer           receipt │ offer
              receipt         receipt │ details
              details

            The offer leads on mobile — burying it under a full receipt is how
            the funnel leaks — but the shop and payment details are reference
            material and belong *after* what they describe. On desktop the
            receipt spans both rows of the left column while the right column
            stacks offer over details.

            Only the receipt is placed explicitly; the two right-column blocks
            auto-flow into the free cells beside it. That matters for a receipt
            with no coin offer: `empty:hidden` takes the offer out of layout
            entirely, and the details slide up into its place instead of leaving
            a hole where a pinned row would have been.
          */}
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 lg:items-start">
            <div class="order-1 lg:order-none lg:sticky lg:top-6 empty:hidden">
              {slots.sidebarTop?.()}
            </div>

            <div class="order-2 lg:order-none lg:col-start-1 lg:row-start-1 lg:row-span-2 lg:col-span-2">
              <PaymentReceiptMain
                payment={props.payment}
                relatedPayment={props.relatedPayment}
                orders={props.orders}
                shop={props.shop}
                labels={receiptLabels.value}
                locale={locale.value || "en"}
              />
            </div>

            <div class="order-3 lg:order-none">
              <div class="space-y-4">
                {/* Shop Information */}
                <div class="bg-base-100 rounded-xl border border-base-300 p-5">
                  <h3 class="font-semibold mb-4 flex items-center gap-2 text-base-content">
                    <span class="material-symbols-outlined text-lg text-primary">
                      storefront
                    </span>
                    {t("customer.receipt.contact_information")}
                  </h3>
                  <div class="space-y-3">
                    {detailRow(
                      "restaurant",
                      t("customer.receipt.restaurant"),
                      <span class="font-medium">{props.shop.name}</span>,
                    )}
                    {detailRow(
                      "location_on",
                      t("customer.receipt.address"),
                      props.shop.address,
                    )}
                    {props.shop.phone &&
                      detailRow(
                        "call",
                        t("customer.receipt.phone"),
                        props.shop.phone,
                      )}
                    {props.shop.email &&
                      detailRow(
                        "mail",
                        t("customer.receipt.email"),
                        props.shop.email,
                      )}
                  </div>
                </div>

                {/* Payment Info */}
                <div class="bg-base-100 rounded-xl border border-base-300 p-5">
                  <h3 class="font-semibold mb-4 flex items-center gap-2 text-base-content">
                    <span class="material-symbols-outlined text-lg text-primary">
                      receipt_long
                    </span>
                    {t("customer.receipt.payment_details")}
                  </h3>
                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between items-center gap-2">
                      <span class="flex items-center gap-1.5 text-base-content/60">
                        <span class="material-symbols-outlined text-base">
                          tag
                        </span>
                        {t("customer.receipt.payment_id")}
                      </span>
                      <span class="font-mono">
                        {props.payment.uuid.slice(0, 8)}
                      </span>
                    </div>
                    <div class="flex justify-between items-center gap-2">
                      <span class="flex items-center gap-1.5 text-base-content/60">
                        <span class="material-symbols-outlined text-base">
                          calendar_today
                        </span>
                        {t("customer.receipt.date")}
                      </span>
                      <span>
                        {new Date(props.payment.created_at).toLocaleDateString(
                          locale.value || "en",
                        )}
                      </span>
                    </div>
                    <div class="flex justify-between items-center gap-2">
                      <span class="flex items-center gap-1.5 text-base-content/60">
                        <span class="material-symbols-outlined text-base">
                          credit_card
                        </span>
                        {t("customer.receipt.method")}
                      </span>
                      <span class="capitalize">{props.payment.method}</span>
                    </div>
                    <div class="flex justify-between items-center gap-2">
                      <span class="flex items-center gap-1.5 text-base-content/60">
                        <span class="material-symbols-outlined text-base">
                          payments
                        </span>
                        {t("customer.receipt.amount")}
                      </span>
                      <span class="font-semibold">
                        {currency.value} {props.payment.amount.toFixed(2)}
                      </span>
                    </div>
                    <div class="flex justify-between items-center gap-2">
                      <span class="flex items-center gap-1.5 text-base-content/60">
                        <span class="material-symbols-outlined text-base">
                          check_circle
                        </span>
                        {t("customer.receipt.status")}
                      </span>
                      <span
                        class={`badge badge-sm ${
                          props.payment.status === "success"
                            ? "badge-success"
                            : "badge-warning"
                        }`}
                      >
                        {props.payment.status}
                      </span>
                    </div>
                  </div>
                </div>

                {slots.sidebarBottom?.()}
              </div>
            </div>
          </div>
        </div>

        {/* Email Modal */}
        {showEmailModal.value && (
          <EmailModal
            onClose={() => (showEmailModal.value = false)}
            onSend={handleEmailSend}
          />
        )}
      </div>
    );
  },
});
